import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';

// Artifact types
export type ArtifactType = 'note' | 'diagram' | 'flashcard' | 'link' | 'snippet' | 'file' | 'mistake_log' | 'video' | 'image';
export type EntityType = 'track' | 'map' | 'lane' | 'pattern' | 'unit' | 'card';
export type VideoType = 'youtube' | 'vimeo' | 'custom';

export interface Artifact {
  id: string;
  userId?: string;
  entityType: EntityType;
  entityId: string;
  artifactType: ArtifactType;
  title?: string;
  content?: string;
  url?: string;
  filePath?: string;
  fileType?: string;
  fileSize?: number;
  question?: string;
  answer?: string;
  language?: string;
  tags: string[];
  isPinned: boolean;
  videoType?: VideoType;
  thumbnailUrl?: string;
  duration?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateArtifactData {
  entityType: EntityType;
  entityId: string;
  artifactType: ArtifactType;
  title?: string;
  content?: string;
  url?: string;
  question?: string;
  answer?: string;
  language?: string;
  tags?: string[];
  isPinned?: boolean;
  videoType?: VideoType;
  thumbnailUrl?: string;
  duration?: number;
}

export interface UpdateArtifactData {
  title?: string;
  content?: string;
  url?: string;
  question?: string;
  answer?: string;
  language?: string;
  tags?: string[];
  isPinned?: boolean;
  videoType?: VideoType;
  thumbnailUrl?: string;
  duration?: number;
}

interface ArtifactsState {
  artifacts: Artifact[];
  entityArtifacts: Record<string, Artifact[]>; // keyed by entityType_entityId
  isLoading: boolean;
  error: string | null;
}

const initialState: ArtifactsState = {
  artifacts: [],
  entityArtifacts: {},
  isLoading: false,
  error: null,
};

// Thunks
export const fetchArtifacts = createAsyncThunk(
  'artifacts/fetchAll',
  async (filters: { entityType?: EntityType; entityId?: string; artifactType?: ArtifactType } | undefined, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters?.entityType) params.append('entityType', filters.entityType);
      if (filters?.entityId) params.append('entityId', filters.entityId);
      if (filters?.artifactType) params.append('artifactType', filters.artifactType);

      const response = await api.get(`/artifacts?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch artifacts');
    }
  }
);

export const fetchEntityArtifacts = createAsyncThunk(
  'artifacts/fetchForEntity',
  async ({ entityType, entityId }: { entityType: EntityType; entityId: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/artifacts/${entityType}/${entityId}`);
      return { entityType, entityId, artifacts: response.data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch entity artifacts');
    }
  }
);

export const createArtifact = createAsyncThunk(
  'artifacts/create',
  async (data: CreateArtifactData, { rejectWithValue }) => {
    try {
      const response = await api.post('/artifacts', data);
      return { ...data, id: response.data.id };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create artifact');
    }
  }
);

export const updateArtifact = createAsyncThunk(
  'artifacts/update',
  async ({ id, data }: { id: string; data: UpdateArtifactData }, { rejectWithValue }) => {
    try {
      await api.put(`/artifacts/${id}`, data);
      return { id, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update artifact');
    }
  }
);

export const deleteArtifact = createAsyncThunk(
  'artifacts/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/artifacts/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete artifact');
    }
  }
);

export const toggleArtifactPin = createAsyncThunk(
  'artifacts/togglePin',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/artifacts/${id}/pin`);
      return { id, isPinned: response.data.isPinned };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to toggle pin');
    }
  }
);

const artifactsSlice = createSlice({
  name: 'artifacts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearEntityArtifacts: (state, action: PayloadAction<{ entityType: EntityType; entityId: string }>) => {
      const key = `${action.payload.entityType}_${action.payload.entityId}`;
      delete state.entityArtifacts[key];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all artifacts
      .addCase(fetchArtifacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArtifacts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.artifacts = action.payload;
      })
      .addCase(fetchArtifacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch entity artifacts
      .addCase(fetchEntityArtifacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntityArtifacts.fulfilled, (state, action) => {
        state.isLoading = false;
        const key = `${action.payload.entityType}_${action.payload.entityId}`;
        state.entityArtifacts[key] = action.payload.artifacts;
      })
      .addCase(fetchEntityArtifacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create artifact
      .addCase(createArtifact.fulfilled, (state, action) => {
        const newArtifact = action.payload as unknown as Artifact;
        state.artifacts.push(newArtifact);
        const key = `${newArtifact.entityType}_${newArtifact.entityId}`;
        if (state.entityArtifacts[key]) {
          state.entityArtifacts[key].push(newArtifact);
        }
      })
      // Update artifact
      .addCase(updateArtifact.fulfilled, (state, action) => {
        const { id, ...data } = action.payload;
        const index = state.artifacts.findIndex(a => a.id === id);
        if (index !== -1) {
          state.artifacts[index] = { ...state.artifacts[index], ...data };
        }
        // Update in entity artifacts
        Object.keys(state.entityArtifacts).forEach(key => {
          const artifacts = state.entityArtifacts[key];
          const idx = artifacts.findIndex(a => a.id === id);
          if (idx !== -1) {
            artifacts[idx] = { ...artifacts[idx], ...data };
          }
        });
      })
      // Delete artifact
      .addCase(deleteArtifact.fulfilled, (state, action) => {
        const id = action.payload;
        state.artifacts = state.artifacts.filter(a => a.id !== id);
        // Remove from entity artifacts
        Object.keys(state.entityArtifacts).forEach(key => {
          state.entityArtifacts[key] = state.entityArtifacts[key].filter(a => a.id !== id);
        });
      })
      // Toggle pin
      .addCase(toggleArtifactPin.fulfilled, (state, action) => {
        const { id, isPinned } = action.payload;
        const artifact = state.artifacts.find(a => a.id === id);
        if (artifact) artifact.isPinned = isPinned;
        // Update in entity artifacts
        Object.keys(state.entityArtifacts).forEach(key => {
          const a = state.entityArtifacts[key].find(a => a.id === id);
          if (a) a.isPinned = isPinned;
        });
      });
  },
});

export const { clearError, clearEntityArtifacts } = artifactsSlice.actions;
export default artifactsSlice.reducer;
